<template>
    <div class="date-input-wrap" @click="showPicker = !showPicker">
        <span v-if="label">{{ label }}</span>
        <div class="date-input">
            <div class="date-input__field">
                <slot name="value-text" :value-text="valueText">
                    {{ valueText.text }}
                </slot>
                <button
                    v-if="date && clearable"
                    type="button"
                    class="date-input__clear"
                    @click.stop="clear"
                >
                    ✕
                </button>
            </div>
            <Datepicker
                :value="date"
                :class="pickerPosition"
                :show="showPicker"
                :max-date="maxDate"
                :min-date="minDate"
                :format="format"
                @close="showPicker = false"
                @dateSelected="onDateSelected"
                @dateChanged="onDateChanged"
            />
            <input
                :id="context.name || $attrs.name"
                v-model="date"
                type="text"
                class="date-input__input"
                :name="context.name || $attrs.name"
                :required="context.required || required"
            />
            <transition name="fade">
                <span v-if="error" class="form-inpit-wrap__error">
                    {{ error[0] }}
                </span>
            </transition>
        </div>
    </div>
</template>

<script>
import Datepicker from '@speechki-frontend/shared/ui/Datepicker';

const noop = () => {};
const empty = () => ({});

const monthsMap = [
    {
        rus: 'янв',
        eng: 'Jan',
    },
    {
        rus: 'фев',
        eng: 'Feb',
    },
    {
        rus: 'мар',
        eng: 'Mar',
    },
    {
        rus: 'апр',
        eng: 'Apr',
    },
    {
        rus: 'май',
        eng: 'May',
    },
    {
        rus: 'июн',
        eng: 'Jun',
    },
    {
        rus: 'июл',
        eng: 'Jul',
    },
    {
        rus: 'авг',
        eng: 'Aug',
    },
    {
        rus: 'сен',
        eng: 'Sep',
    },
    {
        rus: 'окт',
        eng: 'Oct',
    },
    {
        rus: 'ноя',
        eng: 'Nov',
    },
    {
        rus: 'дек',
        eng: 'Dec',
    },
];

export default {
    name: 'DateInput',
    components: {
        Datepicker,
    },
    inject: {
        context: {
            default: empty,
        },
        registerControl: {
            default: () => noop,
        },
    },
    props: {
        label: {
            type: String,
            default: '',
        },
        pickerPosition: {
            type: String,
            default: 'bottom',
        },
        required: {
            type: Boolean,
            default: false,
        },
        error: {
            type: Array,
            default: null,
        },
        modelValue: {},
        clearable: {
            type: Boolean,
            default: false,
        },
        minDate: {
            type: [String, Date, Number],
            default: 0,
        },
        maxDate: {
            type: [String, Date, Number],
            default: 99999999999999,
        },
        format: {
            type: String,
            default: 'yyyy-MM-dd',
        },
    },
    emits: ['update:modelValue', 'change'],

    data() {
        return {
            showPicker: false,
            date: null,
        };
    },
    computed: {
        valueText() {
            const { locale } = this.$i18n;
            let text = this.$t('misc.date');
            let month, monthLoc, date, year;

            if (this.date) {
                const d = new Date(this.date);
                month = d.getMonth();
                date = d.getDate();
                year = d.getFullYear();
                monthLoc = monthsMap[month][locale];
                text = `${date} ${monthLoc} ${year}`;
            }

            return { month, monthLoc, date, year, text };
        },
    },
    watch: {
        modelValue: {
            immidiate: true,
            handler() {
                if (this.modelValue) {
                    this.date =
                        this.modelValue instanceof Date
                            ? this.modelValue
                            : new Date(this.modelValue);
                }
            },
        },
    },
    mounted() {
        this.registerControl(this);
    },
    created() {
        if (this.modelValue) {
            this.date =
                this.modelValue instanceof Date ? this.modelValue : new Date(this.modelValue);
        }
    },
    methods: {
        hidePicker() {
            this.showPicker = false;
        },
        onDateSelected(date) {
            this.date = date;
            this.$emit('update:modelValue', date);
            this.showPicker = false;
        },
        onDateChanged(event) {
            this.$emit('change', event);
        },
        clear() {
            this.date = null;
            this.$emit('update:modelValue', null);
        },
    },
};
</script>

<style lang="scss" src="./DateInput.scss"></style>
